import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput.tsx';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import PermissionGuard from 'components/security/PermissionGuard';
import { DebriefingListElement, DebriefingMin } from 'pages/debriefing/types';
import { useLazyGetMinAssignmentsListWithFiltersQuery } from 'store/api/assignments';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetCorrespondenceMinListQuery } from 'store/api/correspondence.ts';
import { useLazyGetDebriefingsWithFiltersQuery } from 'store/api/debriefings';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { RelationshipTypeCode } from 'types/contacts.ts';
import { FeatureFlag } from 'types/features';
import { renderMatterIndicators, renderMatterOption } from 'utils/matterOptionRenderer';
import { TaskFormState } from './useTaskForm';

/** Task connections: the clients, cases, assignments, correspondence and debriefings the task belongs to. */
export const ConnectionsFields = ({ form }: { form: TaskFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { handleOnContactsChange, handleOnMattersChange, matters, mattersClientsFilter, selectedMatterIds } = form;

    return (
        <>
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="tasks.form.contact"
                placeholder="tasks.form.contact"
                name="clientParties"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: `relationshipTypeCode==${RelationshipTypeCode.CLIENT}`,
                }}
                onChange={handleOnContactsChange}
                PickerModalComponent={ContactPickerModal}
            />
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="tasks.form.cases"
                placeholder="tasks.form.cases"
                name="matters"
                paginated={{
                    fetch: useLazyGetMattersListQuery,
                    searchField: ['fullName', 'clientParty.name'],
                    filters: mattersClientsFilter,
                    sort: ['clientParty.name,asc', 'name,asc'],
                }}
                groupByField="clientParty.name"
                getOptionLabel="fullName"
                getOptionIdentifier="id"
                getSelectedValueStartAdornment={renderMatterIndicators}
                customOptionRenderer={renderMatterOption}
                onChange={handleOnMattersChange}
            />
            {matters && matters.length > 0 && (
                <PermissionGuard
                    permissions={['READ.MATTER_ASSIGNMENTS']}
                    requiredFeature={FeatureFlag.CASE_ASSIGNMENT_ENABLED}
                >
                    <DynamicMultiSelectInput
                        setValue={setValue}
                        control={control}
                        name="assignments"
                        label="tasks.form.assignments"
                        placeholder="tasks.form.select-assignments"
                        paginated={{
                            fetch: useLazyGetMinAssignmentsListWithFiltersQuery,
                            searchField: ['name'],
                            filters: selectedMatterIds ? `matter.id=in=(${selectedMatterIds})` : '',
                            sort: ['matter.fullName,asc', 'name,asc'],
                            pageSize: 500,
                            isEnabled: !!selectedMatterIds,
                        }}
                        groupByField="matter.fullName"
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                    />
                </PermissionGuard>
            )}
            <FeatureFlagged flag={FeatureFlag.CORRESPONDENCE_MODULE} action="show">
                <DynamicMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="tasks.form.correspondence"
                    name="correspondence"
                    paginated={{
                        fetch: useLazyGetCorrespondenceMinListQuery,
                        searchField: ['number', 'name', 'fullName'],
                    }}
                    getOptionLabel="fullName"
                    getOptionIdentifier="id"
                />
            </FeatureFlagged>
            <PermissionGuard permissions={['READ.DEBRIEFINGS.ALL']} requiredFeature={FeatureFlag.DEBRIEFING_MODULE}>
                <DynamicMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="tasks.form.debriefings"
                    name="debriefings"
                    paginated={{
                        fetch: useLazyGetDebriefingsWithFiltersQuery,
                        searchField: ['client.name', 'projectName', 'matters.matter.name'],
                    }}
                    getOptionLabel={debriefingConnectionLabel}
                    getOptionIdentifier="id"
                />
            </PermissionGuard>
        </>
    );
};

// Options come from the list endpoint, stored values are the task's embedded min refs — label both.
const debriefingConnectionLabel = (debriefing: DebriefingListElement | DebriefingMin): string => {
    const firstMatter = 'matters' in debriefing ? debriefing.matters?.[0] : undefined;
    const entity = firstMatter
        ? [firstMatter.caseNumber, firstMatter.name].filter(Boolean).join(' ')
        : (debriefing.clientParty?.name ?? debriefing.projectName ?? '');
    return [entity, debriefing.debriefingDate].filter(Boolean).join(' · ');
};
